import { A11yModule } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { DIALOG_BUTTONS } from './cv-dialog.constants';
import { CvDialogButton } from './cv-dialog.types';

export type { CvDialogButton };

let dialogIdCounter = 0;

/** Legacy's `_alert` defaults to a lone Ok, so this does too. */

/**
 * The app's one modal dialog, reproducing legacy's `Ilit_alert_box` jQuery UI
 * dialog: a centred 450px box over a full-page overlay, with a grey title bar
 * and a button row along the bottom.
 *
 * The row is an ordered array because legacy's is. Everything legacy raises is
 * some arrangement of it - a lone `Ok`, `Yes` then `No`, `Cancel` then `Yes`,
 * `Cancel` then `Confirm`, or nothing at all - and each button says which of the
 * two outputs its click raises rather than the component guessing from position.
 *
 * The body takes projected content, falling back to `message`, so a dialog
 * needing markup does not have to reproduce the chrome.
 *
 * ```html
 * <cv-dialog [message]="error()" (confirmed)="dismiss()" />
 *
 * <cv-dialog
 *   title="Send"
 *   [message]="dueDateMessage()"
 *   [buttons]="[{ label: 'Cancel', role: 'cancel' }, { label: 'Confirm', role: 'confirm' }]"
 *   (confirmed)="send()"
 *   (cancelled)="abort()"
 * />
 * ```
 */
@Component({
  selector: 'cv-dialog',
  standalone: true,
  imports: [A11yModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [':host { display: contents; }'],
  styleUrl: './cv-dialog.component.scss',
  templateUrl: './cv-dialog.component.html',
})
export class CvDialogComponent {
  private readonly id = ++dialogIdCounter;

  /**
   * Legacy takes the title from the page, and every feature page is titled
   * `iLiT`. Only a handful of its dialogs lean on that; the rest name their own,
   * almost always `Alert!`.
   */
  readonly title = input<string>('iLiT');
  /** Body copy. Ignored when content is projected instead. */
  readonly message = input<string>('');
  /** The button row, in the order it should read. `[]` renders no row at all. */
  readonly buttons = input<readonly CvDialogButton[]>(DIALOG_BUTTONS.OK_ONLY);
  /**
   * Legacy dims the page differently per screen: the assignment and planner
   * pages darken it almost to black, everywhere else keeps jQuery UI's pale
   * grey. Light is the default because that is what most screens use.
   */
  readonly backdrop = input<'light' | 'dark'>('light');
  /**
   * Legacy closes every dialog on Escape. Off by default here because the
   * session-expiry and logged-in-elsewhere alerts must not be dismissible.
   */
  readonly dismissOnEscape = input<boolean>(false);
  /**
   * Lifts this instance above every other overlay. Feature overlays reach
   * z-index 100001, over the dialog's usual 99999. Reserved for the
   * single-active-session alert - stacking two defeats the point.
   */
  readonly maxPriority = input<boolean>(false);
  /**
   * Legacy sizes each dialog at its call site — 500, 450 and 400 all appear
   * across the app — so a caller that mirrors a narrower one says so here.
   * 450 is the default because it is the width most of them use.
   */
  readonly width = input<number>(450);
  readonly titleId = input<string>(`cv-dialog-title-${this.id}`);
  readonly bodyId = input<string>(`cv-dialog-body-${this.id}`);

  /** A `confirm` button was pressed. */
  readonly confirmed = output<void>();
  /** A `cancel` button was pressed, or Escape closed the dialog. */
  readonly cancelled = output<void>();
  /** A button with neither role was pressed, like the assessment confirm's View Unanswered. */
  readonly pressed = output<CvDialogButton>();

  protected onButton(button: CvDialogButton): void {
    if (button.role === 'confirm') this.confirmed.emit();
    else if (button.role === 'cancel') this.cancelled.emit();
    else this.pressed.emit(button);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (!this.dismissOnEscape() || event.key !== 'Escape') return;
    // Legacy's Escape closes without running the affirmative button's handler.
    event.preventDefault();
    this.cancelled.emit();
  }
}
